import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { FlaskConical, LogOut, Menu, UserRound } from 'lucide-react';
import { Button, cn, Dialog, Select } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useT } from '../lib/i18n';
import { usePermissions, useSession } from '../lib/session';
import { useSystemName } from '../lib/system-name';
import { StoreChoiceProvider } from '../lib/store-choice';
import { aiNavGroup } from '../pages/ai/nav';
import { EXPORT_PERMISSIONS } from '../pages/exports';
import { useMeta } from '../pages/team';
import { TopBar } from './TopBar';
import { useReportsNavGroup } from './reports/nav';

interface NavItem {
  to: string;
  label: string;
  permission?: string;
  /** Bu yetkilerden biri yeterli (ör. Dışa aktarma: herhangi bir listeyi görme yetkisi). */
  anyOf?: string[];
}

function useNav(): Array<{ group: string; items: NavItem[] }> {
  const t = useT();
  const reports = useReportsNavGroup();
  return [
    {
      group: t('admin.nav.overview'),
      items: [
        { to: '/', label: t('admin.nav.dashboard') },
        { to: '/kurulum', label: t('admin.nav.setup'), permission: 'org.settings.read' },
      ],
    },
    reports,
    {
      group: t('admin.nav.sales'),
      items: [
        { to: '/siparisler', label: t('admin.nav.orders'), permission: 'orders.order.read' },
        { to: '/odemeler', label: t('admin.nav.payments'), permission: 'payments.payment.read' },
        { to: '/hediye-kartlari', label: t('admin.nav.giftCards'), permission: 'payments.giftcard.read' },
        { to: '/faturalar', label: t('admin.nav.invoices'), permission: 'invoices.invoice.read' },
        { to: '/musteriler', label: t('admin.nav.customers'), permission: 'customers.customer.read' },
        { to: '/bayiler', label: t('admin.nav.dealers'), permission: 'b2b.dealer.read' },
        { to: '/bayilik-basvurulari', label: t('admin.nav.applications'), permission: 'b2b.dealer.read' },
        { to: '/satis-noktalari', label: t('admin.nav.salesPoints'), permission: 'b2b.dealer.read' },
        { to: '/cari-hesaplar', label: t('admin.nav.ledger'), permission: 'b2b.ledger.read' },
        { to: '/teklifler', label: t('admin.nav.quotes'), permission: 'b2b.quote.read' },
        { to: '/bolgeler', label: t('admin.nav.salesRegions'), permission: 'b2b.dealer.read' },
        { to: '/komisyonlar', label: t('admin.nav.commissions'), permission: 'b2b.commission.read' },
        { to: '/bayi-raporlari', label: t('admin.nav.dealerReports'), permission: 'reports.sales.read' },
        {
          to: '/ulke-raporu',
          label: t('admin.nav.internationalReport'),
          permission: 'reports.sales.read',
        },
        { to: '/duzenli-siparisler', label: t('admin.nav.subscriptions'), permission: 'orders.order.read' },
        {
          to: '/destek-talepleri',
          label: t('admin.nav.supportTickets'),
          permission: 'customers.ticket.read',
        },
        { to: '/canli-sohbet', label: t('admin.nav.liveChat'), permission: 'customers.chat.read' },
        {
          to: '/iletisim-mesajlari',
          label: t('admin.nav.contactMessages'),
          permission: 'customers.customer.read',
        },
        { to: '/kvkk-basvurulari', label: t('admin.nav.privacy'), permission: 'privacy.request.read' },
        { to: '/saklama-imha', label: t('admin.nav.retention'), permission: 'privacy.retention.read' },
        { to: '/disa-aktarma', label: t('admin.nav.exports'), anyOf: EXPORT_PERMISSIONS },
      ],
    },
    {
      group: t('admin.nav.catalog'),
      items: [
        { to: '/urunler', label: t('admin.nav.products'), permission: 'catalog.product.read' },
        { to: '/kategoriler', label: t('admin.nav.categories'), permission: 'catalog.product.read' },
        { to: '/koleksiyonlar', label: t('admin.nav.collections'), permission: 'catalog.product.read' },
        { to: '/urun-ozellikleri', label: t('admin.nav.attributes'), permission: 'catalog.product.read' },
        { to: '/sorular', label: t('admin.nav.questions'), permission: 'catalog.question.read' },
        {
          to: '/urun-degerlendirmeleri',
          label: t('admin.nav.reviews'),
          permission: 'catalog.review.read',
        },
        { to: '/stok', label: t('admin.nav.stock'), permission: 'inventory.stock.read' },
        { to: '/fiyatlar', label: t('admin.nav.pricing'), permission: 'pricing.price.read' },
        { to: '/doviz-kurlari', label: t('admin.nav.fx'), permission: 'pricing.price.read' },
        { to: '/kuponlar', label: t('admin.nav.discounts'), permission: 'pricing.price.read' },
        {
          to: '/otomatik-indirimler',
          label: t('admin.nav.promotions'),
          permission: 'pricing.price.read',
        },
        { to: '/ice-aktarma', label: t('admin.nav.imports'), permission: 'catalog.import' },
      ],
    },
    {
      group: t('admin.nav.storefrontGroup'),
      items: [
        { to: '/tasarim', label: t('admin.nav.design'), permission: 'theme.read' },
        { to: '/sayfa-tasarimlari', label: t('admin.nav.pageDesigns'), permission: 'theme.read' },
        { to: '/menuler', label: t('admin.nav.menus'), permission: 'content.read' },
        { to: '/sayfalar', label: t('admin.nav.pages'), permission: 'content.read' },
        { to: '/bannerlar', label: t('admin.nav.banners'), permission: 'content.read' },
        { to: '/blog', label: t('admin.nav.blog'), permission: 'content.read' },
        { to: '/ihtiyac-bulucu', label: t('admin.nav.needsFinder'), permission: 'content.read' },
        { to: '/icerik-takvimi', label: t('admin.nav.contentCalendar'), permission: 'content.read' },
        { to: '/icerik-raporu', label: t('admin.nav.contentReport'), permission: 'content.read' },
        { to: '/formlar', label: t('admin.nav.forms'), permission: 'content.read' },
        { to: '/seo', label: t('admin.nav.seo'), permission: 'seo.read' },
        { to: '/sayfa-hizi', label: t('admin.nav.webVitals'), permission: 'seo.read' },
        { to: '/arama-performansi', label: t('admin.nav.searchConsole'), permission: 'seo.read' },
        { to: '/anahtar-kelimeler', label: t('admin.nav.keywords'), permission: 'seo.read' },
        { to: '/site-ici-arama', label: t('admin.nav.siteSearch'), permission: 'seo.read' },
        { to: '/google-alisveris', label: t('admin.nav.merchant'), permission: 'seo.read' },
        {
          to: '/google-raporlari',
          label: t('admin.nav.googleReports'),
          anyOf: ['tracking.read', 'seo.read'],
        },
        { to: '/olcumleme', label: t('admin.nav.tracking'), permission: 'tracking.read' },
        { to: '/kaynaklar', label: t('admin.nav.attribution'), permission: 'tracking.read' },
      ],
    },
    {
      group: t('admin.nav.marketing'),
      items: [
        {
          to: '/segmentler',
          label: t('admin.nav.segments'),
          permission: 'marketing.audience.read',
        },
        {
          to: '/e-posta-kampanyalari',
          label: t('admin.nav.campaigns'),
          permission: 'marketing.campaign.read',
        },
        {
          to: '/otomasyonlar',
          label: t('admin.nav.automations'),
          permission: 'marketing.campaign.read',
        },
        {
          to: '/stok-ve-fiyat-bildirimleri',
          label: t('admin.nav.productAlerts'),
          permission: 'marketing.campaign.read',
        },
        {
          to: '/e-posta-sablonlari',
          label: t('admin.nav.emailTemplates'),
          permission: 'marketing.campaign.read',
        },
        {
          to: '/sms-whatsapp-kampanyalari',
          label: t('admin.nav.smsWhatsapp'),
          permission: 'marketing.campaign.read',
        },
        {
          to: '/sms-sablonlari',
          label: t('admin.nav.smsTemplates'),
          permission: 'marketing.campaign.read',
        },
        {
          to: '/anlik-bildirimler',
          label: t('admin.nav.push'),
          permission: 'marketing.campaign.read',
        },
        { to: '/sadakat', label: t('admin.nav.loyalty'), permission: 'marketing.campaign.read' },
        {
          to: '/tavsiye-programi',
          label: t('admin.nav.referrals'),
          permission: 'marketing.campaign.read',
        },
        { to: '/is-ortaklari', label: t('admin.nav.affiliates'), permission: 'marketing.campaign.read' },
        { to: '/reklam-merkezi', label: t('admin.nav.ads'), permission: 'marketing.ads.read' },
        {
          to: '/reklam-merkezi/google-ads',
          label: t('admin.nav.googleAdsCampaigns'),
          permission: 'marketing.ads.read',
        },
        {
          to: '/pazarlama-izinleri',
          label: t('admin.nav.marketingAudience'),
          permission: 'marketing.audience.read',
        },
      ],
    },
    aiNavGroup(t),
    {
      group: t('admin.nav.business'),
      items: [
        { to: '/isletme', label: t('admin.nav.organization'), permission: 'org.settings.read' },
        { to: '/markalar', label: t('admin.nav.brands'), permission: 'org.brand.read' },
        { to: '/magazalar', label: t('admin.nav.stores'), permission: 'org.store.read' },
        { to: '/odeme-ayarlari', label: t('admin.nav.paymentSettings'), permission: 'payments.payment.read' },
        { to: '/kargo', label: t('admin.nav.shipping'), permission: 'org.store.read' },
        { to: '/yasal-metinler', label: t('admin.nav.legal'), permission: 'legal.document.read' },
        { to: '/yonlendirmeler', label: t('admin.nav.redirects'), permission: 'seo.read' },
        { to: '/kirik-linkler', label: t('admin.nav.brokenLinks'), permission: 'seo.read' },
      ],
    },
    {
      group: t('admin.nav.team'),
      items: [
        { to: '/kullanicilar', label: t('admin.nav.users'), permission: 'org.user.read' },
        { to: '/roller', label: t('admin.nav.roles'), permission: 'org.user.read' },
      ],
    },
    {
      group: t('admin.nav.connections'),
      items: [
        { to: '/entegrasyonlar', label: t('admin.nav.integrations'), permission: 'org.integration.read' },
        { to: '/google', label: t('admin.nav.google'), permission: 'org.integration.read' },
        { to: '/bizimhesap', label: t('admin.nav.bizimhesap'), permission: 'org.integration.read' },
        { to: '/api-anahtarlari', label: t('admin.nav.apiKeys'), permission: 'org.api_key.manage' },
        { to: '/webhooklar', label: t('admin.nav.webhooks'), permission: 'org.webhook.manage' },
      ],
    },
    {
      group: t('admin.nav.records'),
      items: [
        { to: '/denetim', label: t('admin.nav.audit'), permission: 'org.audit.read' },
        { to: '/sistem', label: t('admin.nav.system'), permission: 'org.system.read' },
        { to: '/tasima-paketi', label: t('admin.nav.migration'), permission: 'org.data.migrate' },
        { to: '/yardim-metinleri', label: t('admin.nav.helpEditor'), permission: 'org.help.manage' },
        { to: '/ceviriler', label: t('admin.nav.uiTranslations'), permission: 'org.help.manage' },
      ],
    },
  ];
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const nav = useNav();
  const { can } = usePermissions();
  const path = useRouterState({ select: (s) => s.location.pathname });
  // Etkin öğe: adresle eşleşen en uzun yol (ör. /reklam-merkezi/google-ads açıkken yalnızca o; Reklam merkezi değil).
  const matches = (to: string) => (to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`));
  const activeTo = nav
    .flatMap((g) => g.items.map((i) => i.to))
    .filter(matches)
    .sort((a, b) => b.length - a.length)[0];
  return (
    <nav className="space-y-5">
      {nav.map((g) => {
        const items = g.items.filter(
          (i) => (!i.permission || can(i.permission)) && (!i.anyOf || i.anyOf.some((p) => can(p))),
        );
        if (items.length === 0) return null;
        return (
          <div key={g.group}>
            <p className="mb-1.5 px-3 font-cond text-[0.8125rem] font-medium text-ink-3">{g.group}</p>
            <ul className="space-y-0.5">
              {items.map((i) => {
                const active = i.to === activeTo;
                return (
                  <li key={i.to}>
                    <Link
                      to={i.to}
                      onClick={onNavigate}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'relative block rounded-sm px-3 py-2 text-[0.9375rem] text-ink-2 hover:bg-surface-2 hover:text-ink',
                        active && 'bg-pine-tint font-medium text-pine hover:bg-pine-tint hover:text-pine',
                      )}
                    >
                      {active && (
                        <span
                          className="absolute inset-y-1.5 start-0 w-[3px] rounded-full bg-pine"
                          aria-hidden
                        />
                      )}
                      {i.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

function OrgSwitcher() {
  const t = useT();
  const session = useSession();
  const qc = useQueryClient();
  const list = session.data?.memberships ?? [];
  const current = session.data?.session.activeOrganizationId ?? '';
  if (list.length <= 1) {
    return <p className="truncate px-3 text-sm text-ink-2">{list[0]?.organizationName}</p>;
  }
  return (
    <Select
      aria-label={t('admin.nav.organizationSwitch')}
      value={current}
      onChange={async (e) => {
        await api.post('/v1/auth/switch-organization', { organizationId: e.target.value });
        await qc.invalidateQueries();
      }}
    >
      {list.map((m) => (
        <option key={m.organizationId} value={m.organizationId}>
          {m.organizationName}
        </option>
      ))}
    </Select>
  );
}

function Brandmark() {
  const name = useSystemName();
  return (
    <Link to="/" className="flex items-center gap-2.5 px-3">
      <svg viewBox="0 0 32 32" className="h-8 w-8 shrink-0" aria-hidden>
        <rect width="32" height="32" rx="7" className="fill-pine" />
        <path
          d="M9 10h14l-11 12h11"
          fill="none"
          className="stroke-on-pine"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="font-cond text-[1.0625rem] font-semibold tracking-[-0.01em] text-ink">{name}</span>
    </Link>
  );
}

function UserBox() {
  const t = useT();
  const session = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  return (
    <div className="flex items-center gap-1 border-t border-line px-2 pt-3">
      <Link
        to="/hesabim"
        className="flex min-w-0 flex-1 items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-surface-2"
      >
        <UserRound className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
        <span className="truncate text-sm text-ink">{session.data?.user.name}</span>
      </Link>
      <Button
        variant="ghost"
        size="icon"
        aria-label={t('common.signOut')}
        title={t('common.signOut')}
        onClick={async () => {
          await api.post('/v1/auth/sign-out').catch(() => undefined);
          qc.clear();
          await navigate({ to: '/giris' });
        }}
      >
        <LogOut className="h-4 w-4" aria-hidden />
      </Button>
    </div>
  );
}

/** Staging sunucusunda her sayfanın üstünde: e-postalar yalnızca izin listesine gider (F3B-08). */
function EnvironmentBanner() {
  const t = useT();
  const meta = useMeta();
  const { can } = usePermissions();
  if (!meta.data?.mailGuard?.active) return null;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber/30 bg-amber-tint px-4 py-2 text-sm text-ink lg:px-8 print:hidden"
    >
      <FlaskConical className="h-4 w-4 shrink-0 text-amber" aria-hidden />
      <span>{t('admin.nav.stagingBanner')}</span>
      {can('org.system.read') && (
        <Link
          to="/sistem"
          hash="staging-eposta"
          className="font-medium text-pine underline underline-offset-2"
        >
          {t('admin.nav.stagingBannerLink')}
        </Link>
      )}
      <HelpTip k="system.staging_banner" />
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <StoreChoiceProvider>
      <div className="min-h-dvh lg:grid lg:grid-cols-[16rem_1fr] print:block">
        <a
          href="#icerik"
          className="sr-only focus:not-sr-only focus:absolute focus:start-3 focus:top-3 focus:z-50 focus:rounded-sm focus:bg-surface focus:px-3 focus:py-2"
        >
          {t('admin.nav.skipToContent')}
        </a>
        <aside className="sticky top-0 hidden h-dvh flex-col border-e border-line bg-surface lg:flex print:hidden">
          <div className="space-y-2 px-2 pb-4 pt-5">
            <Brandmark />
            <OrgSwitcher />
          </div>
          {/* Menü uzarsa yalnızca bu bölüm kayar; hesap kutusu her zaman görünür kalır. */}
          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
            <NavList />
          </div>
          <div className="pb-3">
            <UserBox />
          </div>
        </aside>
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-line bg-surface/95 px-3 py-2 backdrop-blur lg:hidden print:hidden">
          <Brandmark />
          <Button variant="ghost" size="icon" aria-label={t('admin.nav.menu')} onClick={() => setOpen(true)}>
            <Menu className="h-5 w-5" aria-hidden />
          </Button>
        </header>
        <Dialog
          open={open}
          onOpenChange={setOpen}
          title={t('admin.nav.menu')}
          closeLabel={t('common.close')}
          variant="sheet"
        >
          <div className="space-y-5">
            <OrgSwitcher />
            <NavList onNavigate={() => setOpen(false)} />
            <UserBox />
          </div>
        </Dialog>
        <main id="icerik" className="min-w-0">
          <EnvironmentBanner />
          <TopBar />
          {children}
        </main>
      </div>
    </StoreChoiceProvider>
  );
}
